
export enum UserRole {
  SUPERADMIN = 'SuperAdmin',
  ADMIN = 'Admin',
  EDITOR = 'Editor',
  LEADERSHIP = 'Leadership'
}

export interface GamificationConfig {
  contact_points: number;
  attendance_points: number;
}

export const PLAN_LIMITS = {
  essential: {
    contacts: 5000,
    storageGB: 5,
    label: 'Essencial',
    priceMonthly: 197.00,
    priceYearly: 1499.00,
    installmentYearly: 124.90
  },
  advanced: {
    contacts: 50000,
    storageGB: 50,
    label: 'Avançado',
    priceMonthly: 459.00,
    priceYearly: 3899.00,
    installmentYearly: 324.90
  },
  pro: {
    contacts: Infinity,
    storageGB: 100,
    label: 'Pro',
    priceMonthly: 899.00,
    priceYearly: 6580.00,
    installmentYearly: 548.33
  },
  betatester: {
    contacts: Infinity,
    storageGB: 100,
    label: 'Beta Tester',
    priceMonthly: 0,
    priceYearly: 0,
    installmentYearly: 0
  }
};

export interface Container {
  id: string;
  name: string;
  slug: string;
  admin_email: string;
  created_at: string;
  plan: 'essential' | 'advanced' | 'pro' | 'betatester';
  status: 'active' | 'pending' | 'suspended';
  billing_cycle: 'monthly' | 'yearly';
  asaas_customer_id?: string;
  current_subscription_id?: string;
  billing_tax_id?: string;
  expires_at?: string | null; 
  billing_address?: {
    postalCode: string;
    address: string;
    number: string;
    complement?: string;
    province: string;
    city: string;
    state: string;
  };
  gamification?: GamificationConfig;
  disabled_sectors?: string[];
}

export interface EmailOverride {
  id: string;
  email: string;
  duration_days: number;
  created_at: string;
  created_by: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: UserRole;
  container_id: string;
  status: string;
  created_at: string;
}

export interface PlanRequest {
  id: string;
  container_id: string;
  requested_by: string;
  request_type: 'change_plan' | 'cancel_subscription';
  target_plan?: 'essential' | 'advanced' | 'pro' | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export interface UserPermissions {
  canEditDocuments: boolean;
  canEditAttendances: boolean;
  canEditCRM: boolean;
  canEditTasks: boolean;
  canEditOwnContent?: boolean;
  canAccessWhatsApp?: boolean;
}

export interface UserSettings {
  birthday_templates?: string[];
  pending_cabinet_request?: {
    name: string;
    slug: string;
    plan: 'essential' | 'advanced' | 'pro';
    billing_cycle: 'monthly' | 'yearly';
    requested_at: string;
    billing_info?: any;
    is_override?: boolean;
    expires_at?: string;
    asaas_customer_id?: string;
    payment_id?: string;
  } | null;
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  container_id: string | null;
  permissions: UserPermissions;
  settings?: UserSettings;
  avatar?: string;
}

export interface WhatsAppAssessor {
  id: string;
  container_id: string;
  name: string;
  phone: string;
  linked_user_id?: string | null;
  active: boolean;
  created_at: string;
}

export interface WhatsAppGroupHub {
  id: string;
  container_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WhatsAppGroupHubMember {
  id: string;
  hub_id: string;
  group_jid: string;
  group_name?: string;
  added_at: string;
}

export interface WhatsAppGroupMessage {
  id: string;
  container_id: string;
  hub_id?: string;
  group_jid?: string;
  message_content: any;
  sent_at: string;
  sent_by?: string;
  total_groups: number;
  total_participants: number;
}

export interface WhatsAppGroupMessageAnalytics {
  id: string;
  message_id: string;
  group_jid: string;
  participant_jid: string;
  participant_name?: string;
  viewed: boolean;
  viewed_at?: string;
  reacted: boolean;
  reaction?: string;
  reacted_at?: string;
  responded: boolean;
  response_text?: string;
  responded_at?: string;
}

export interface WhatsAppBroadcastList {
  id: string;
  container_id: string;
  wa_id?: string;
  name: string;
  description?: string;
  recipient_count: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WhatsAppBroadcastMessage {
  id: string;
  container_id: string;
  broadcast_list_id?: string;
  message_content: any;
  sent_at: string;
  sent_by?: string;
  total_recipients: number;
}

export interface WhatsAppBroadcastAnalytics {
  id: string;
  message_id: string;
  recipient_jid: string;
  recipient_name?: string;
  delivered: boolean;
  delivered_at?: string;
  viewed: boolean;
  viewed_at?: string;
  reacted: boolean;
  reaction?: string;
  reacted_at?: string;
  responded: boolean;
  response_text?: string;
  responded_at?: string;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  type?: 'file' | 'image';
  size?: number;
  timestamp: string;
}

export interface Contact {
  id: string;
  container_id: string;
  created_by?: string;
  name: string;
  type: string;
  support_level?: string;
  profession?: string;
  birthdate?: string;
  phones: string[];
  emails: string[];
  city: string;
  neighborhood: string;
  street?: string;
  number?: string;
  complement?: string;
  zipcode?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  tags: string[];
  observations: string;
  contact_origin?: string;
  voter_registration_number?: string;
  voter_zone?: string;
  voter_section?: string;
  photo_url?: string | null;
  created_at?: string;
  deleted_at?: string | null;
}

export enum TaskStatus {
  TODO = 'A Fazer',
  IN_PROGRESS = 'Em andamento',
  WAITING = 'Aguardando',
  DONE = 'Concluído',
  ARCHIVED = 'Arquivado'
}

export enum TaskPriority {
  LOW = 'Baixa',
  MEDIUM = 'Média',
  HIGH = 'Alta',
  URGENT = 'Urgente'
}

export type TaskSector = string;

export interface CustomSector {
  id: string;
  name: string;
  is_active: boolean;
}

export const DEFAULT_TASK_SECTORS: TaskSector[] = [
  'Geral', 'Jurídico', 'Comunicação', 'Saúde', 'Educação', 'Infraestrutura'
];

export enum AttendanceStatus {
  PENDING = 'Pendente',
  IN_PROGRESS = 'Em andamento',
  SCHEDULED = 'Agendado',
  DONE = 'Concluído'
}

export interface AttendanceUpdate {
  id: string;
  userId: string;
  timestamp: string;
  text: string;
}

export interface TaskComment {
  id: string;
  userId: string;
  timestamp: string;
  text: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface Attendance {
  id: string;
  container_id: string;
  created_by?: string;
  contactid: string;
  demand: string;
  responsible_id: string;
  channel: string;
  origin_details?: string;
  urgency: string;
  summary: string;
  status: AttendanceStatus;
  attachments: (string | Attachment)[];
  updates: AttendanceUpdate[];
  comments?: TaskComment[];
  created_at: string;
  deleted_at?: string | null;
  source?: 'internal' | 'public_form';
  viewed_at?: string | null;
}

export interface TaskHistory {
  id: string;
  userId: string;
  timestamp: string;
  action: string;
}

export interface Task {
  id: string;
  container_id: string;
  title: string;
  description: string;
  sector: string;
  status: TaskStatus;
  priority: TaskPriority;
  duedate?: string; 
  assignees: string[];
  stakeholders?: string[];
  contactid?: string | null;
  tags: string[];
  history: TaskHistory[];
  comments: TaskComment[];
  attachments: Attachment[];
  checklist?: ChecklistItem[];
  deleted_at?: string | null;
}

export enum DocumentStatus {
  ELABORATING = 'Em Elaboração',
  FILED = 'Protocolado',
  ANSWERED = 'Respondido',
  CANCELED = 'Cancelado',
  ARCHIVED = 'Arquivado'
}

export enum DocumentResult {
  IGNORED = 'Ignorado',
  PARTIAL = 'Atendido Parcialmente',
  FULFILLED = 'Atendido',
  UNFEASIBLE = 'Inviável',
  DENIED = 'Negado',
  ARCHIVED = 'Arquivado'
}

export interface Document {
  id: string;
  container_id: string;
  responsible_id?: string | null;
  date: string;
  document_number: string;
  document_type: string;
  requester_type: 'contact' | 'user';
  requester_id: string;
  made_by_id: string;
  subject: string;
  recipient: string;
  status: DocumentStatus;
  emailRecipients: string[];
  attachments: (string | Attachment)[];
  updates: string;
  response_text?: string;
  response_date?: string;
  comments: TaskComment[];
  history: TaskHistory[];
  deleted_at?: string | null;
  theme?: string;
  resultado?: DocumentResult | null;
  received_by?: string | null;
  internal_requester?: string | null;
  deadline?: string | null;
  notify_requester?: boolean;
  notify_updates?: boolean;
  stakeholders?: string[];
  address_street?: string;
  address_number?: string;
  address_neighborhood?: string;
  address_city?: string;
  address_state?: string;
  address_zipcode?: string;
  latitude?: number;
  longitude?: number;
}

export interface Notification {
  id: string;
  user_id?: string;
  type: 'mention' | 'birthday' | 'deadline' | 'overdue' | 'task_update' | 'assignment' | 'system_alert';
  title: string;
  description: string;
  timestamp: string;
  read: boolean;
  related_id?: string;
}

export interface Institution {
  id: string;
  name: string;
  type: string;
  latitude: number;
  longitude: number;
  address?: string;
  container_id: string | null;
  created_at?: string;
}

export interface SessionLog {
  id: string;
  user_id: string;
  event_type: 'login' | 'logout';
  timestamp: string;
}

export interface AuditEvent {
  id: string;
  container_id: string;
  user_id: string;
  action: string;
  entity_type: string;
  entity_name: string;
  entity_id?: string;
  timestamp: string;
}
